import {
  AvailabilityType,
  DateChoicesEnum,
  KindOfTimeType,
  OrderLimitType,
  ProductOverrideType,
  WorkingDayType,
} from '@/constants/enum';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import {
  IShippingDisable,
  IShippingOverrideDateRanges,
  IShippingOverrideHolidays,
  IShippingOverrideSpecificWorkingDays,
  IShippingSettings,
  IShippingState,
} from '@/types/components/shipping';
import { DEFAULT_ORDER_LIMIT } from '@/constants';
import { ISpecificWorkingDay, IHoliday, IWeeklyWorkingDay } from '@/types/components/day';

const shippingSettingInitial: IShippingSettings = {
  id: -1,
  shop: '',
  name: '',
  city: '',
  additionalNote: '',
  locationId: -1,
  isActive: false,
  isActiveDatePicker: false,
  shippingChoice: DateChoicesEnum.DATETIMESLOT,
  workingDayType: WorkingDayType.WEEKDAYS,
  shippingTimeSlotType: KindOfTimeType.SAME,
  shippingPreparationTimeType: KindOfTimeType.SAME,
  orderLimitType: OrderLimitType.LIMIT_PER_DAY,
  shippingDateRange: { value: '14', unit: 'day' }, // ex: { value: 1, unit: 'day' }
  overrideType: ProductOverrideType.OVERRIDE_SPECIFIC_WORKING_DAYS,
  productAvailabilityType: AvailabilityType.ALL,
  isEnablePreparationTime: false,
  additionalTags: [], // ex: [] tag1, tag2, tag3 ]
  minimumOrderAmount: null,
  updatedAt: '',
  createdAt: '',
};

const shippingStateInitial: IShippingState = {
  isSaving: false,
  isLoading: false,
  isCheckingTimeSlot: false,
};

const shippingDisableInitial: IShippingDisable = {
  isDisableSpecificProduct: false,
  isDisableProductOverride: false,
  isDisableOrderLimitPerTimeSlot: false,
  isDisableMinimumOrderValue: false,
};

export const DEFAULT_WEEKLY_WORKING_DATA_SHIPPING = [-1, 0, 1, 2, 3, 4, 5, 6].map((item) => ({
  id: item,
  isEnable: true,
  day: item,
  preparationTime: {
    value: '1',
    unit: 'day',
  },
  isEnableCutOffTime: false,
  cutOffTime: '18:00',
  cutOffPreparationTime: {
    value: '2',
    unit: 'day',
  },
  shippingDateRange: {
    value: '14',
    unit: 'day',
  },
  exactTimes: [
    {
      start: '14:00',
      orderLimit: 0,
    },
    {
      start: '16:00',
      orderLimit: 0,
    },
  ],
  timeSlots: [
    {
      start: '09:00',
      end: '16:00',
      orderLimit: DEFAULT_ORDER_LIMIT,
    },
  ],
  orderLimitPerDay: DEFAULT_ORDER_LIMIT,
  workingHours: { start: '09:00', end: '18:00' },
}));

const shippingWeeklyWorkingDaysInitial: IWeeklyWorkingDay[] = [];

const shippingSpecificWorkingDaysInitial: ISpecificWorkingDay[] = [];

const shippingHolidayInitial: IHoliday[] = [];

const shippingProductsInitial: string[] = [];

const shippingCollectionsInitial: string[] = [];

const shippingOverrideSpecificWorkingDaysInitial: IShippingOverrideSpecificWorkingDays[] = [];

const shippingOverrideHolidayInitial: IShippingOverrideHolidays[] = [];

const shippingOverrideDateRangesInitial: IShippingOverrideDateRanges[] = [];

const initialState = {
  shipping: {
    shippingSettings: shippingSettingInitial,
    shippingState: shippingStateInitial,
    shippingWeeklyWorkingDays: shippingWeeklyWorkingDaysInitial,
    shippingWeeklyWorkingDaysLastState: shippingWeeklyWorkingDaysInitial,
    shippingSpecificWorkingDays: shippingSpecificWorkingDaysInitial,
    shippingHolidays: shippingHolidayInitial,
    shippingProducts: shippingProductsInitial,
    shippingCollections: shippingCollectionsInitial,
    shippingOverrideSpecificWorkingDays: shippingOverrideSpecificWorkingDaysInitial,
    shippingOverrideHolidays: shippingOverrideHolidayInitial,
    shippingOverrideDateRanges: shippingOverrideDateRangesInitial,
  },
  shippingBackup: {
    shippingSettings: shippingSettingInitial,
    shippingState: shippingStateInitial,
    shippingWeeklyWorkingDays: shippingWeeklyWorkingDaysInitial,
    shippingWeeklyWorkingDaysLastState: shippingWeeklyWorkingDaysInitial,
    shippingSpecificWorkingDays: shippingSpecificWorkingDaysInitial,
    shippingHolidays: shippingHolidayInitial,
    shippingProducts: shippingProductsInitial,
    shippingCollections: shippingCollectionsInitial,
    shippingOverrideSpecificWorkingDays: shippingOverrideSpecificWorkingDaysInitial,
    shippingOverrideHolidays: shippingOverrideHolidayInitial,
    shippingOverrideDateRanges: shippingOverrideDateRangesInitial,
  },
  shippingDisable: shippingDisableInitial,
};

const shippingSlice = createSlice({
  name: 'shipping',
  initialState,
  reducers: {
    handleSaveShippingSettings: (state, action: PayloadAction<Partial<IShippingSettings>>) => {
      state.shipping.shippingSettings = { ...state.shipping.shippingSettings, ...action.payload };
    },
    handleSetShippingState: (state, action: PayloadAction<Partial<IShippingState>>) => {
      state.shipping.shippingState = { ...state.shipping.shippingState, ...action.payload };
    },
    handleSaveShippingWeeklyWorkingDays: (state, action: PayloadAction<IWeeklyWorkingDay[]>) => {
      state.shipping.shippingWeeklyWorkingDays = action.payload;
    },
    handleSaveShippingWeeklyWorkingDaysLastState: (state, action: PayloadAction<IWeeklyWorkingDay[]>) => {
      state.shipping.shippingWeeklyWorkingDaysLastState = action.payload;
    },
    handleSaveShippingSpecificWorkingDays: (state, action: PayloadAction<ISpecificWorkingDay[]>) => {
      state.shipping.shippingSpecificWorkingDays = action.payload;
    },
    handleSaveShippingHolidays: (state, action: PayloadAction<IHoliday[]>) => {
      state.shipping.shippingHolidays = action.payload;
    },
    handleSaveShippingProducts: (state, action: PayloadAction<string[]>) => {
      state.shipping.shippingProducts = action.payload;
    },
    handleSaveShippingCollections: (state, action: PayloadAction<string[]>) => {
      state.shipping.shippingCollections = action.payload;
    },
    handleSaveOverrideShippingSpecificWorkingDays: (state, action: PayloadAction<IShippingOverrideSpecificWorkingDays[]>) => {
      state.shipping.shippingOverrideSpecificWorkingDays = action.payload;
    },
    handleSaveOverrideShippingHolidays: (state, action: PayloadAction<IShippingOverrideHolidays[]>) => {
      state.shipping.shippingOverrideHolidays = action.payload;
    },
    handleSaveOverrideShippingDateRanges: (state, action: PayloadAction<IShippingOverrideDateRanges[]>) => {
      state.shipping.shippingOverrideDateRanges = action.payload;
    },

    handleSaveBackupShippingSettings: (state, action: PayloadAction<Partial<IShippingSettings>>) => {
      state.shippingBackup.shippingSettings = { ...state.shippingBackup.shippingSettings, ...action.payload };
    },
    handleSetBackupShippingState: (state, action: PayloadAction<Partial<IShippingState>>) => {
      state.shippingBackup.shippingState = { ...state.shippingBackup.shippingState, ...action.payload };
    },
    handleSaveBackupShippingWeeklyWorkingDays: (state, action: PayloadAction<IWeeklyWorkingDay[]>) => {
      state.shippingBackup.shippingWeeklyWorkingDays = action.payload;
    },
    handleSaveBackupShippingWeeklyWorkingDaysLastState: (state, action: PayloadAction<IWeeklyWorkingDay[]>) => {
      state.shippingBackup.shippingWeeklyWorkingDaysLastState = action.payload;
    },
    handleSaveBackupShippingSpecificWorkingDays: (state, action: PayloadAction<ISpecificWorkingDay[]>) => {
      state.shippingBackup.shippingSpecificWorkingDays = action.payload;
    },
    handleSaveBackupShippingHolidays: (state, action: PayloadAction<IHoliday[]>) => {
      state.shippingBackup.shippingHolidays = action.payload;
    },
    handleSaveBackupShippingProducts: (state, action: PayloadAction<string[]>) => {
      state.shippingBackup.shippingProducts = action.payload;
    },
    handleSaveBackupShippingCollections: (state, action: PayloadAction<string[]>) => {
      state.shippingBackup.shippingCollections = action.payload;
    },
    handleSaveBackupOverrideShippingSpecificWorkingDays: (
      state,
      action: PayloadAction<IShippingOverrideSpecificWorkingDays[]>,
    ) => {
      state.shippingBackup.shippingOverrideSpecificWorkingDays = action.payload;
    },
    handleSaveBackupOverrideShippingHolidays: (state, action: PayloadAction<IShippingOverrideHolidays[]>) => {
      state.shippingBackup.shippingOverrideHolidays = action.payload;
    },
    handleSaveBackupOverrideShippingDateRanges: (state, action: PayloadAction<IShippingOverrideDateRanges[]>) => {
      state.shippingBackup.shippingOverrideDateRanges = action.payload;
    },

    handleResetShippingInitial: (state) => {
      state.shipping = initialState.shipping;
      state.shippingBackup = initialState.shippingBackup;
    },
    handleResetShippingState: (state) => {
      state.shipping.shippingState.isSaving = shippingStateInitial.isSaving;
      state.shipping.shippingState.isLoading = shippingStateInitial.isLoading;
      state.shipping.shippingState.isCheckingTimeSlot = shippingStateInitial.isCheckingTimeSlot;
    },
    handleResetShipping: (state) => {
      state.shipping = state.shippingBackup;
    },
    handleSetShippingDisable: (state, action: PayloadAction<Partial<IShippingDisable>>) => {
      state.shippingDisable = { ...state.shippingDisable, ...action.payload };
    },
  },
});

export const shippingSettingSelector = createSelector(
  (state: RootState) => state.shipping,
  (state) => state.shipping.shippingSettings,
);
export const shippingStateSelector = createSelector(
  (state: RootState) => state.shipping,
  (state) => state.shipping.shippingState,
);
export const shippingWeeklyWorkingDaySelector = createSelector(
  (state: RootState) => state.shipping,
  (state) => state.shipping.shippingWeeklyWorkingDays,
);
export const shippingWeeklyWorkingDaysLastStateSelector = createSelector(
  (state: RootState) => state.shipping,
  (state) => state.shipping.shippingWeeklyWorkingDaysLastState,
);
export const shippingSpecificWorkingDaySelector = createSelector(
  (state: RootState) => state.shipping,
  (state) => state.shipping.shippingSpecificWorkingDays,
);
export const shippingHolidaySelector = createSelector(
  (state: RootState) => state.shipping,
  (state) => state.shipping.shippingHolidays,
);
export const shippingProductSelector = createSelector(
  (state: RootState) => state.shipping,
  (state) => state.shipping.shippingProducts,
);
export const shippingCollectionSelector = createSelector(
  (state: RootState) => state.shipping,
  (state) => state.shipping.shippingCollections,
);
export const shippingOverrideSpecificWorkingDaysSelector = createSelector(
  (state: RootState) => state.shipping,
  (state) => state.shipping.shippingOverrideSpecificWorkingDays,
);
export const shippingOverrideHolidaysSelector = createSelector(
  (state: RootState) => state.shipping,
  (state) => state.shipping.shippingOverrideHolidays,
);
export const shippingOverrideDateRangesSelector = createSelector(
  (state: RootState) => state.shipping,
  (state) => state.shipping.shippingOverrideDateRanges,
);

export const currentShippingSelector = createSelector(
  (state: RootState) => state.shipping,
  (shipping) => shipping.shipping,
);
export const oldShippingSelector = createSelector(
  (state: RootState) => state.shipping,
  (state) => state.shippingBackup,
);
export const shippingDisableSelector = createSelector(
  (state: RootState) => state.shipping.shippingDisable,
  (shippingDisable) => shippingDisable,
);

export const {
  handleSaveShippingSettings,
  handleSetShippingState,
  handleSaveShippingWeeklyWorkingDays,
  handleSaveShippingWeeklyWorkingDaysLastState,
  handleSaveShippingSpecificWorkingDays,
  handleSaveShippingHolidays,
  handleSaveShippingProducts,
  handleSaveShippingCollections,
  handleSaveOverrideShippingSpecificWorkingDays,
  handleSaveOverrideShippingHolidays,
  handleSaveOverrideShippingDateRanges,
  handleSaveBackupShippingSettings,
  handleSetBackupShippingState,
  handleSaveBackupShippingWeeklyWorkingDays,
  handleSaveBackupShippingWeeklyWorkingDaysLastState,
  handleSaveBackupShippingSpecificWorkingDays,
  handleSaveBackupShippingHolidays,
  handleSaveBackupShippingProducts,
  handleSaveBackupShippingCollections,
  handleSaveBackupOverrideShippingSpecificWorkingDays,
  handleSaveBackupOverrideShippingHolidays,
  handleSaveBackupOverrideShippingDateRanges,
  handleResetShippingState,
  handleResetShippingInitial,
  handleResetShipping,
  handleSetShippingDisable,
} = shippingSlice.actions;

export default shippingSlice;
